// Render-lock system for the optimistic cart.
// Prevents renderContents() from replacing DOM while optimistic operations
// are in flight. When all locks release, a single final render fires.

import type {
	CartAddWithSections,
	CartUpdateWithSections,
} from '@/scripts/core/cart-functions'
import { cartDebug, summarizeCartItems } from '@/scripts/cart/optimistic-debug'

export type LockToken = {
	id: number
	name: string
}

type PendingResponse = CartAddWithSections | CartUpdateWithSections

const LOCK_TIMEOUT_MS = 15_000

let _nextId = 0
const _activeLocks = new Map<
	number,
	{ name: string; timer: ReturnType<typeof setTimeout> }
>()
let _pendingResponse: PendingResponse | null = null
let _onAllReleasedCallback: (() => void) | null = null

/** Acquire a lock that prevents renderContents from replacing DOM. */
export function acquireLock(name: string): LockToken {
	const id = ++_nextId
	cartDebug('optimistic-lock:acquire', {
		id,
		name,
		activeLocksBefore: _activeLocks.size,
	})
	const timer = setTimeout(() => {
		console.warn(
			`[OptimisticLock] Lock "${name}" (${id}) timed out after ${LOCK_TIMEOUT_MS}ms — force releasing`
		)
		const token = { id, name }
		releaseLock(token)
	}, LOCK_TIMEOUT_MS)

	_activeLocks.set(id, { name, timer })
	return { id, name }
}

/** Release a lock. When the last lock releases, fires the onAllReleased callback. */
export function releaseLock(token: LockToken): void {
	const entry = _activeLocks.get(token.id)
	if (!entry) return
	cartDebug('optimistic-lock:release', {
		id: token.id,
		name: token.name,
		activeLocksBefore: _activeLocks.size,
	})
	clearTimeout(entry.timer)
	_activeLocks.delete(token.id)

	if (_activeLocks.size === 0 && _onAllReleasedCallback) {
		cartDebug('optimistic-lock:all-released-callback')
		const cb = _onAllReleasedCallback
		_onAllReleasedCallback = null
		cb()
	}
}

/** Check if any locks are currently held. */
export function isLocked(): boolean {
	return _activeLocks.size > 0
}

/** Store the latest server response for deferred rendering. */
export function setPendingResponse(response: PendingResponse): void {
	_pendingResponse = response
	cartDebug('optimistic-lock:set-pending-response', {
		itemCount: 'items' in response ? response.items.length : undefined,
		items:
			'items' in response
				? summarizeCartItems(response.items)
				: undefined,
		hasSections: Boolean(response.sections),
	})
}

/** Get and clear the stored pending response. */
export function getPendingResponse(): PendingResponse | null {
	const r = _pendingResponse
	_pendingResponse = null
	cartDebug('optimistic-lock:get-pending-response', {
		hadResponse: Boolean(r),
		itemCount: r && 'items' in r ? r.items.length : undefined,
	})
	return r
}

/**
 * Register a callback to fire when all locks are released.
 * Replaces any previously registered callback.
 */
export function setOnAllReleasedCallback(cb: () => void): void {
	_onAllReleasedCallback = cb
	cartDebug('optimistic-lock:set-on-all-released-callback')
}

/** Force-release all locks (used for error recovery). */
export function forceReleaseAllLocks(): void {
	cartDebug('optimistic-lock:force-release-all', {
		activeLocks: _activeLocks.size,
	})
	for (const [, entry] of _activeLocks) {
		clearTimeout(entry.timer)
	}
	_activeLocks.clear()
	_pendingResponse = null
	_onAllReleasedCallback = null
}
